import * as React from 'react';

import {
  Button,
  DataGridColumn,
  HeaderIcons,
  LocalizedDate,
  Menu,
} from '@rematter/ui-web';
import { LucideMoreHorizontal } from 'lucide-react';

import { BookingRecord } from '../../../types/booking';
import { ContainersBadgeList } from '../../_shared/ContainersBadgeList';
import { BookingStatusBadge } from '../../_shared/StatusBadges';

import { useBookingDetailViewMenuItems } from './useBookingDetailViewMenuItems';

import { useTranslations } from '~/modules/trade/i18n/useTranslations';

type Args = {
  selectedStatus: string;
  onDoubleClick?: (props: { row: BookingRecord }) => void;
  setIsLoadDialogOpen?: (open: boolean, bookingId?: string) => void;
  setSelectedBooking?: (booking: BookingRecord | null) => void;
  onVoid?: (booking: BookingRecord) => void;
  onRefetch?: () => void;
};

// Component for the action menu cell
export type BookingActionMenuProps = {
  booking: BookingRecord;
  setIsLoadDialogOpen?: (open: boolean, bookingId?: string) => void;
  setSelectedBooking?: (booking: BookingRecord | null) => void;
  onVoid?: (booking: BookingRecord) => void;
  onRefetch?: () => void;
};

const BookingActionMenu = ({
  booking,
  setIsLoadDialogOpen,
  setSelectedBooking,
  onVoid,
  onRefetch,
}: BookingActionMenuProps) => {
  const { items, renderTrigger, isDialogOpen } = useBookingDetailViewMenuItems({
    booking,
    setIsLoadDialogOpen: (open: boolean) => {
      if (open && setSelectedBooking) {
        setSelectedBooking(booking);
      }
      setIsLoadDialogOpen?.(open);
    },
    visibility: 'always', // Default
    displayListActions: true,
    onVoid,
    onRefetch,
  });

  return (
    <div
      className="@BookingActionMenu relative flex justify-center"
      data-menu-open={isDialogOpen || undefined}
    >
      <Menu
        trigger={renderTrigger(
          <Button
            size="icon"
            icon={<LucideMoreHorizontal />}
            variant="outline"
          />,
        )}
        items={items}
        highlightOnOpen
      />
    </div>
  );
};

// Utility function to truncate text
const truncateText = (text: string): string => {
  if (!text) {
    return '-';
  }
  return text;
};

const ENABLED_COLUMNS_BY_STATUS_KEY: Record<string, string[]> = {
  all: [
    'bookingNumber',
    'salesOrderNumber',
    'counterpartyReferenceNumber',
    'destinationPort',
    'containers',
    'cutOffDate',
    'earlyReturnDate',
    'vesselName',
    'createdAt',
    'notes',
    'actions',
  ],
  draft: [
    'bookingNumber',
    'salesOrderNumber',
    'counterpartyReferenceNumber',
    'destinationPort',
    'containers',
    'cutOffDate',
    'earlyReturnDate',
    'vesselName',
    'createdAt',
    'notes',
    'actions',
  ],
  open: [
    'bookingNumber',
    'salesOrderNumber',
    'counterpartyReferenceNumber',
    'destinationPort',
    'containers',
    'cutOffDate',
    'earlyReturnDate',
    'vesselName',
    'createdAt',
    'notes',
    'actions',
  ],
  shipped: [
    'shipmentDate',
    'bookingNumber',
    'salesOrderNumber',
    'counterpartyReferenceNumber',
    'numberOfLoads',
    'shippedNetWeight',
    'shippedTotal',
    'createdAt',
    'actions',
  ],
  closed: [
    'shipmentDate',
    'bookingNumber',
    'salesOrderNumber',
    'counterpartyReferenceNumber',
    'numberOfLoads',
    'shippedNetWeight',
    'shippedTotal',
    'status',
    'closedOn',
    'actions',
  ],
};

export const useBookingsListColumns = ({
  selectedStatus,
  onDoubleClick,
  setIsLoadDialogOpen,
  setSelectedBooking,
  onVoid,
  onRefetch,
}: Args) => {
  const t = useTranslations();

  const columns: DataGridColumn<BookingRecord>[] = React.useMemo(
    () => [
      {
        key: 'shipmentDate',
        header: t.bookingsView.table.columns.shipmentDate,
        icon: HeaderIcons.Date,
        width: 150,
        resizable: true,
        onDoubleClick,
        cell: () => '-', // TODO: Add shipment date field
      },
      {
        key: 'bookingNumber',
        header: t.bookingsView.table.columns.bookingNumber,
        icon: HeaderIcons.Text,
        width: 150,
        resizable: true,
        sortable: true,
        align: 'left',
        onDoubleClick,
        cell: ({ row }: { row: BookingRecord }) => row.bookingNumber ?? '-',
      },
      {
        key: 'salesOrderNumber',
        header: t.bookingsView.table.columns.salesOrderNumber,
        icon: HeaderIcons.Text,
        width: 150,
        resizable: true,
        align: 'left',
        onDoubleClick,
        cell: ({ row }: { row: Partial<BookingRecord> }) =>
          row.salesOrders?.length
            ? row.salesOrders.map((salesOrder, index) => (
                <span key={salesOrder.id}>
                  {salesOrder.documentNumber}
                  {index < (row.salesOrders?.length ?? 0) - 1 && ', '}
                </span>
              ))
            : '-',
      },
      {
        key: 'counterpartyReferenceNumber',
        header: t.bookingsView.table.columns.counterpartyReferenceNumber,
        icon: HeaderIcons.Text,
        width: 150,
        resizable: true,
        align: 'left',
        onDoubleClick,
        cell: ({ row }: { row: Partial<BookingRecord> }) =>
          row.salesOrders?.length
            ? row.salesOrders.map((salesOrder) => (
                <span key={salesOrder.id}>
                  {salesOrder.counterpartyReferenceNumber}
                </span>
              ))
            : '-',
      },
      {
        key: 'destinationPort',
        header: t.bookingsView.table.columns.portOfDestination,
        icon: HeaderIcons.Text,
        width: 180,
        resizable: true,
        align: 'left',
        onDoubleClick,
        cell: ({ row }: { row: Partial<BookingRecord> }) =>
          row.destinationPort?.name ?? '-',
      },
      {
        key: 'containers',
        header: t.bookingsView.table.columns.containers,
        icon: HeaderIcons.Text,
        width: 200,
        resizable: true,
        onDoubleClick,
        cell: ({ row }: { row: Partial<BookingRecord> }) => (
          <ContainersBadgeList id={row.id} containers={row.containers ?? []} />
        ),
      },
      {
        key: 'cutOffDate',
        header: t.bookingsView.table.columns.cutoffDate,
        icon: HeaderIcons.Date,
        width: 150,
        resizable: true,
        sortable: true,
        onDoubleClick,
        align: 'left',
        cell: ({ row }: { row: Partial<BookingRecord> }) => (
          <LocalizedDate date={row.cutOffDate} />
        ),
      },
      {
        key: 'earlyReturnDate',
        header: t.bookingsView.table.columns.earlyReturnDate,
        icon: HeaderIcons.Date,
        width: 150,
        resizable: true,
        align: 'left',
        onDoubleClick,
        cell: ({ row }: { row: Partial<BookingRecord> }) => (
          <LocalizedDate date={row.earlyReturnDate} />
        ),
      },
      {
        key: 'vesselName',
        header: t.bookingsView.table.columns.vessel,
        icon: HeaderIcons.Text,
        width: 150,
        resizable: true,
        align: 'left',
        onDoubleClick,
        cell: ({ row }: { row: Partial<BookingRecord> }) =>
          row.vesselName ?? '-',
      },
      {
        key: 'numberOfLoads',
        header: t.bookingsView.table.columns.numberOfLoads,
        icon: HeaderIcons.Number,
        width: 150,
        resizable: true,
        align: 'left',
        onDoubleClick,
        cell: ({ row }: { row: Partial<BookingRecord> }) =>
          row.loads?.length ? row.loads.length : '-',
      },
      {
        key: 'shippedNetWeight',
        header: t.bookingsView.table.columns.shippedNetWeight,
        icon: HeaderIcons.Number,
        width: 150,
        resizable: true,
        align: 'left',
        onDoubleClick,
        cell: ({ row }: { row: Partial<BookingRecord> }) =>
          row.loads?.length
            ? row.loads.reduce((acc, load) => acc + load.netWeight, 0)
            : '-',
      },
      {
        key: 'shippedTotal',
        header: t.bookingsView.table.columns.shippedTotal,
        icon: HeaderIcons.Number,
        width: 150,
        resizable: true,
        align: 'left',
        onDoubleClick,
        cell: ({ row }: { row: Partial<BookingRecord> }) =>
          row.loads?.length
            ? row.loads.reduce((acc, load) => acc + load.total, 0)
            : '-',
      },
      {
        key: 'createdAt',
        header: t.bookingsView.table.columns.createdOn,
        icon: HeaderIcons.Date,
        width: 150,
        resizable: true,
        sortable: true,
        align: 'left',
        onDoubleClick,
        cell: ({ row }: { row: Partial<BookingRecord> }) =>
          row.timestamps?.createdAt ? (
            <LocalizedDate date={row.timestamps?.createdAt} />
          ) : (
            '-'
          ),
      },
      {
        key: 'notes',
        header: t.bookingsView.table.columns.notes,
        icon: HeaderIcons.Text,
        width: 200,
        resizable: true,
        onDoubleClick,
        cell: ({ row }: { row: Partial<BookingRecord> }) => (
          <div
            className="line-clamp-3 max-w-32 overflow-hidden text-left"
            title={row.internalNotes ?? '-'}
          >
            {truncateText(row.internalNotes ?? '-')}
          </div>
        ),
      },
      {
        key: 'status',
        header: t.bookingsView.table.columns.status,
        icon: HeaderIcons.Text,
        width: 150,
        resizable: true,
        sortable: true,
        align: 'left',
        onDoubleClick,
        cell: ({ row }: { row: Partial<BookingRecord> }) =>
          row.status ? <BookingStatusBadge status={row.status} /> : '-',
      },
      {
        key: 'closedOn',
        header: t.bookingsView.table.columns.closedOn,
        icon: HeaderIcons.Date,
        width: 150,
        resizable: true,
        align: 'left',
        onDoubleClick,
        cell: ({ row }: { row: Partial<BookingRecord> }) =>
          //TODO: Replace with a proper closed on field
          row.timestamps?.updatedAt ? (
            <LocalizedDate date={row.timestamps?.updatedAt} />
          ) : (
            '-'
          ),
      },
      {
        key: 'actions',
        header: '',
        width: 60,
        pinned: 'right',
        cell: ({ row }: { row: Partial<BookingRecord> }) => (
          <BookingActionMenu
            booking={row as BookingRecord}
            setIsLoadDialogOpen={(open: boolean) => {
              setIsLoadDialogOpen?.(open, row.id);
            }}
            setSelectedBooking={setSelectedBooking}
            onVoid={onVoid}
            onRefetch={onRefetch}
          />
        ),
      },
    ],
    [
      t,
      onDoubleClick,
      setIsLoadDialogOpen,
      setSelectedBooking,
      onVoid,
      onRefetch,
    ],
  );

  const columnVisibilityState = React.useMemo(() => {
    const statusKey = selectedStatus || 'all';
    const enabledColumns =
      ENABLED_COLUMNS_BY_STATUS_KEY[statusKey] ??
      ENABLED_COLUMNS_BY_STATUS_KEY['all'];

    return Object.fromEntries(
      columns.map((column) => [
        column.key,
        enabledColumns.includes(column.key),
      ]),
    );
  }, [selectedStatus, columns]);

  return { columns, columnVisibilityState };
};
